import { Head, useForm } from "@inertiajs/react"
import { useRef, useState } from "react"

import AppLayout from "@/layouts/app-layout"
import { todayStr } from "@/lib/date-utils"
import { createExpenseEquipmentLogsPath, equipmentLogsPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

interface SelectOption {
  id: number
  name: string
}

interface PaymentMethod {
  value: string
  label: string
}

interface Props {
  equipment_options: SelectOption[]
  vendor_options: SelectOption[]
  payment_methods: PaymentMethod[]
  employee_options: SelectOption[]
  can_select_employee: boolean
  preselected_equipment_id: string
  errors?: Record<string, string[]> | null
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Equipment", href: "/equipment" },
  { title: "Maintenance Logs", href: "/equipment_logs" },
  { title: "Add Expense", href: "#" },
]

export default function NewExpense({
  equipment_options,
  vendor_options,
  payment_methods,
  employee_options,
  can_select_employee,
  preselected_equipment_id,
  errors,
}: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [vendorId, setVendorId] = useState("")

  const { data, setData, post, processing, transform } = useForm({
    equipment_id: preselected_equipment_id ?? "",
    vendor_id: "",
    user_id: "",
    log_date: todayStr(),
    paid_by: "",
    amount: "",
    paid_to: "",
    notes: "",
    start_odometer: "",
    end_odometer: "",
    receipt: null as File | null,
  })

  transform((data) => ({ equipment_log: data }))

  const handleVendorChange = (value: string) => {
    setVendorId(value)
    setData("vendor_id", value)
    // When vendor is selected, clear paid_to (vendor name takes precedence)
    if (value) {
      setData((prev) => ({ ...prev, vendor_id: value, paid_to: "" }))
    } else {
      setData("vendor_id", value)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post(createExpenseEquipmentLogsPath(), { forceFormData: true })
  }

  const inputClass =
    "mt-1 block w-full rounded border border-hpi-light-gray px-3 py-2 focus:border-hpi-active-blue focus:outline-none"
  const labelClass = "block text-sm font-medium text-hpi-dark-gray"

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Add Equipment Expense" />

      <div className="p-6">
        <h1 className="mb-6 text-2xl font-semibold">Add Equipment Expense</h1>

        <form onSubmit={handleSubmit} className="max-w-2xl space-y-4">
          {/* Equipment */}
          <div>
            <label className={labelClass}>Equipment *</label>
            <select
              value={data.equipment_id}
              onChange={(e) => setData("equipment_id", e.target.value)}
              className={inputClass}
            >
              <option value="">-- Select Equipment --</option>
              {equipment_options.map((eq) => (
                <option key={eq.id} value={eq.id}>
                  {eq.name}
                </option>
              ))}
            </select>
            {errors?.equipment_id && (
              <p className="text-hpi-red mt-1 text-sm">
                {errors.equipment_id[0]}
              </p>
            )}
          </div>

          {/* Date */}
          <div>
            <label className={labelClass}>Date *</label>
            <input
              type="date"
              value={data.log_date}
              onChange={(e) => setData("log_date", e.target.value)}
              className={inputClass}
            />
            {errors?.log_date && (
              <p className="text-hpi-red mt-1 text-sm">{errors.log_date[0]}</p>
            )}
          </div>

          {/* Employee (only for users with reportForOthers permission) */}
          {can_select_employee && (
            <div>
              <label className={labelClass}>Employee</label>
              <select
                value={data.user_id}
                onChange={(e) => setData("user_id", e.target.value)}
                className={inputClass}
              >
                <option value="">-- Current User --</option>
                {employee_options.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Payment Method */}
          <div>
            <label className={labelClass}>Payment Method *</label>
            <select
              value={data.paid_by}
              onChange={(e) => setData("paid_by", e.target.value)}
              className={inputClass}
            >
              <option value="">-- Select Payment Method --</option>
              {payment_methods.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
            {errors?.paid_by && (
              <p className="text-hpi-red mt-1 text-sm">{errors.paid_by[0]}</p>
            )}
          </div>

          {/* Amount */}
          <div>
            <label className={labelClass}>Amount *</label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={data.amount}
              onChange={(e) => setData("amount", e.target.value)}
              className={inputClass}
              placeholder="0.00"
            />
            {errors?.amount && (
              <p className="text-hpi-red mt-1 text-sm">{errors.amount[0]}</p>
            )}
          </div>

          {/* Vendor */}
          <div>
            <label className={labelClass}>Vendor</label>
            <select
              value={vendorId}
              onChange={(e) => handleVendorChange(e.target.value)}
              className={inputClass}
            >
              <option value="">-- None --</option>
              {vendor_options.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
            <p className="text-hpi-med-gray mt-1 text-xs">
              Select a vendor or enter &quot;Paid To&quot; below
            </p>
          </div>

          {/* Paid To (only when no vendor selected) */}
          {!vendorId && (
            <div>
              <label className={labelClass}>Paid To *</label>
              <input
                type="text"
                maxLength={100}
                value={data.paid_to}
                onChange={(e) => setData("paid_to", e.target.value)}
                className={inputClass}
                placeholder="Who was paid?"
              />
              {errors?.paid_to && (
                <p className="text-hpi-red mt-1 text-sm">{errors.paid_to[0]}</p>
              )}
            </div>
          )}

          {/* Odometer */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Start Odometer / Hours</label>
              <input
                type="number"
                step="0.01"
                value={data.start_odometer}
                onChange={(e) => setData("start_odometer", e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>End Odometer / Hours</label>
              <input
                type="number"
                step="0.01"
                value={data.end_odometer}
                onChange={(e) => setData("end_odometer", e.target.value)}
                className={inputClass}
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className={labelClass}>Explanation *</label>
            <textarea
              value={data.notes}
              onChange={(e) => setData("notes", e.target.value)}
              rows={6}
              className={inputClass}
              placeholder="Explain the expense..."
            />
            {errors?.notes && (
              <p className="text-hpi-red mt-1 text-sm">{errors.notes[0]}</p>
            )}
          </div>

          {/* Receipt Upload */}
          <div>
            <label className={labelClass}>Receipt</label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              className="text-hpi-dark-gray file:bg-hpi-dark-blue hover:file:bg-hpi-blue mt-1 block w-full text-sm file:mr-4 file:rounded file:border-0 file:px-4 file:py-2 file:text-sm file:font-medium file:text-white"
              onChange={(e) => {
                const file = e.target.files?.[0] ?? null
                setData("receipt", file)
              }}
            />
            <p className="text-hpi-med-gray mt-1 text-xs">
              PDF, JPEG, or PNG up to 10 MB
            </p>
            {errors?.receipt && (
              <p className="text-hpi-red mt-1 text-sm">{errors.receipt[0]}</p>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-4 pt-4">
            <a
              href={equipmentLogsPath()}
              className="border-hpi-light-gray hover:bg-hpi-pale-blue inline-block rounded border px-4 py-2"
            >
              Cancel
            </a>
            <button
              type="submit"
              disabled={processing}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white disabled:opacity-50"
            >
              {processing ? "Saving..." : "Add Expense"}
            </button>
          </div>
        </form>
      </div>
    </AppLayout>
  )
}
